import {ReactReduxContext, useDispatch} from "react-redux";
import React, {useContext} from "react";
import {setStep4Housing} from "../../actions";
import {
    CAMPUS_BERKELEY,
    HAS_DEPENDENTS_NO,
    HAS_DEPENDENTS_YES,
    HOUSING_ON_CAMPUS,
    INDEX_DIRECT,
    INDEX_INDIRECT, NON_RESIDENT_SCHOLARSHIP, NON_RESIDENT_SCHOLARSHIP_HIGH, NON_RESIDENT_SCHOLARSHIP_LOW,
    PARENT_MARITAL_STATUS_MARRIED,
    PARENT_MARITAL_STATUS_SINGLE,
    RESIDENCY_CALIFORNIA, RESIDENCY_OTHER,
    SELF_HELP,
    SELF_HELP_DEBT_FREE,
    SELF_HELP_RANGE_HIGH,
    SELF_HELP_RANGE_LOW,
    STATUS_DEPENDENT,
    STATUS_INDEPENDENT,
    STUDENT_MARITAL_STATUS_MARRIED,
    STUDENT_MARITAL_STATUS_SINGLE
} from "../steps/bi/constants/MAGIC_NUMBERS";

import {
    NON_RESIDENT_SCHOLARSHIP_TEXT,
    RESULT_GIFT_ELIGIBILITY_HELP_TEXT,
    RESULT_NET_COST_HELP_TEXT,
    RESULT_SAI_HELP_TEXT,
    RESULT_TOTAL_COST_HELP_TEXT
} from "../steps/bi/constants/INFO_TEXT";

import {ATTENDANCE_COSTS} from "../steps/bi/constants/ATTENDANCE_COSTS";
import {DEBUG_MODE} from "../steps/bi/constants/DEBUG_MODE";
import FORM_DATA from "../steps/bi/constants/FORM_DATA";

import {PanelHeader} from "../panelHeader";
import {PanelList} from "../panelList";
import {PanelSubtext} from "../panelSubtext";
import {GetCosts} from "./GetCosts";
import {faeDebugger} from "../faeDebugger";

/**
 * AidPanel component presents the overall values for
 *  - Estimated Student Aid Index (SAI)
 *  - Estimated Total Cost of Attendance (COA)
 *  - Estimated Gift Eligibility (EGE)
 *  - Estimated Net Cost (ENC)
 *
 *  SAI is determined from input in Step 2
 *  COA is determined on campus and housing
 *  EGE is determined by campus
 *  ENC is COA - EGE
 *
 *  EGE and ENC may be a single value, or a range.
 *
 *  These values can be recalculated by changing the selected values
 *  in the campus and housing {@link Step4DropDown} elements in the
 *  {@link Step4} container of this AidPanel component.
 * @constructor
 */
export const AidPanel = () => {

    const dispatch = useDispatch();
    const context = useContext(ReactReduxContext);
    const update = () => context.store.getState().app.stepData;
    const stepData = GetCosts(update());

    const directCosts = [
        'ucTuition', 'campusFees', 'healthInsurance'
    ];

    const indirectCosts = [
        'booksAndSupplies', 'personalAndTransportation'
    ];

    let v = update().step2;
    dispatch(setStep4Housing(v));

    const HOUSING_AND_MEALS_DIRECT = stepData['housingAndMeals'][INDEX_DIRECT];
    const HOUSING_AND_MEALS_INDIRECT = stepData['housingAndMeals'][INDEX_INDIRECT];
    if (update().step4.backup2.housing === HOUSING_ON_CAMPUS) {
        directCosts.push('housingAndMeals');
        stepData['housingAndMeals'] = HOUSING_AND_MEALS_DIRECT
    } else {
        stepData['housingAndMeals'] = HOUSING_AND_MEALS_INDIRECT;
        indirectCosts.unshift('housingAndMeals');
    }

    const directCostsData = directCosts.map(
        item => {
            const costText = FORM_DATA.costsList.find(cost => cost.value === item)?.text || '';
            const stepValue = stepData[item] || 0;
            return {title: costText, value: stepValue};
        });

    const indirectCostsData = indirectCosts.map(
        item => {
            const costText = FORM_DATA.costsList.find(cost => cost.value === item)?.text || '';
            const stepValue = stepData[item] || 0;
            return {title: costText, value: stepValue};
        });

    const calcDirectCosts = {
        'title': 'Direct Costs',
        'data': directCostsData
    };
    const calcIndirectCosts = {
        'title': 'Indirect Costs',
        'data': indirectCostsData
    };

    const estimatedTotal =
        directCostsData.reduce((acc, current) => {
            return acc + current.value;
        }, 0) +
        indirectCostsData.reduce((acc, current) => {
            return acc + current.value;
        }, 0);

    let showSAI = false;
    let saiModification = 0;
    let estimatedGift = [];
    let finalNetCost = [];
    let povertyModifiedSAI = 0;
    let scholarships = [];

    //  TODO: Remove all the logging debug before final commit
    if(update().step2.residency === RESIDENCY_CALIFORNIA) {
        //  We're going to modify the SAI for future use
        povertyModifiedSAI = ModifySAIPovertyThreshold(stepData.estimatedStudentAidIndex);

        //  Californians don't get gifts.  Yet.
        estimatedGift = (stepData.checks.data.step2.residency !== RESIDENCY_CALIFORNIA)
            ? [0]
            : step4CalculateEge(stepData.checks.data.step2, povertyModifiedSAI);

        if (estimatedGift.length === 2 && estimatedGift[0] === estimatedGift[1]) {
            estimatedGift = [estimatedGift[0]];
        }

        //  If SAI is less than or equal to 0, treat it as 0 for determining gift eligibility.
        saiModification = povertyModifiedSAI <= 0
            ? 0
            : povertyModifiedSAI;

        estimatedGift = estimatedGift
            .map(e => estimatedTotal - saiModification - e)
            .map(e => e < 0 ? 0 : e);

        finalNetCost = estimatedGift
            .map(e => estimatedTotal - e)
            .map(e => e < 0 ? 0 : e);

        estimatedGift.sort((a,b) => a - b);
        finalNetCost.sort((a,b) => a - b);

        if (estimatedGift.length === 2 && estimatedGift[0] === estimatedGift[1]) {
            estimatedGift = [estimatedGift[0]];
        }
        if (finalNetCost.length === 2 && finalNetCost[0] === finalNetCost[1]) {
            finalNetCost = [finalNetCost[0]];
        }

        showSAI = true;
    } else {
        estimatedGift = [
            ATTENDANCE_COSTS[RESIDENCY_OTHER][update().step2.campus][NON_RESIDENT_SCHOLARSHIP][NON_RESIDENT_SCHOLARSHIP_LOW],
            ATTENDANCE_COSTS[RESIDENCY_OTHER][update().step2.campus][NON_RESIDENT_SCHOLARSHIP][NON_RESIDENT_SCHOLARSHIP_HIGH]
        ].sort();

        console.log('estimatedGift', estimatedGift);

        if (estimatedGift.length === 2 && estimatedGift[0] === estimatedGift[1]) {
            estimatedGift = [estimatedGift[0]];
        }

        estimatedGift.sort((a,b) => a - b);
        finalNetCost = estimatedGift
            .map(e => estimatedTotal- e)
            .map(e => e < 0 ? 0 : e);

        finalNetCost.sort((a,b) => a - b);
        scholarships = estimatedGift;

        if (estimatedGift.length === 2 && estimatedGift[0] === estimatedGift[1]) {
            estimatedGift = [estimatedGift[0]];
        }

        if (finalNetCost.length === 2 && finalNetCost[0] === finalNetCost[1]) {
            finalNetCost = [finalNetCost[0]];
        }

    }

    let baseSort = (a, b) => {
        return a - b;
    };

    estimatedGift.sort(baseSort);
    finalNetCost.sort(baseSort);

    if (estimatedGift.length === 2 && estimatedGift[0] === estimatedGift[1]) {
        estimatedGift = [estimatedGift[0]];
    }
    if (finalNetCost.length === 2 && finalNetCost[0] === finalNetCost[1]) {
        finalNetCost = [finalNetCost[0]];
    }

    return (
        <div id="fae-results">
            <ul aria-label="UC Costs Estimate">
                {showSAI &&
                    <li>
                        <PanelHeader text="Estimated Student Aid Index (SAI)" value={povertyModifiedSAI}
                                     leader={true} helpText={RESULT_SAI_HELP_TEXT} useDollarSign={false}/>
                    </li>
                }
                <li>
                    <PanelHeader text="Estimated Total Cost of Attendance" value={estimatedTotal} leader={true}
                                 helpText={RESULT_TOTAL_COST_HELP_TEXT}/>
                    <ul aria-label="Total Costs Breakdown">
                        <PanelList contents={calcDirectCosts} id="panelList1" ariaLabel="Direct Costs Breakdown"/>
                        <PanelList contents={calcIndirectCosts} id="panelList2" ariaLabel="Indirect Costs Breakdown"/>
                    </ul>
                </li>
                <li>
                    <PanelHeader text="Estimated Gift Eligibility" value={estimatedGift} leader={true}
                                 helpText={RESULT_GIFT_ELIGIBILITY_HELP_TEXT}/>
                    {!showSAI &&
                    <ul>
                        <li>
                            <PanelSubtext text="Grants" value={0} subText={NON_RESIDENT_SCHOLARSHIP_TEXT} />
                        </li>
                        <li>
                            <PanelSubtext text="Scholarships" value={scholarships}/>
                        </li>

                    </ul>
                    }
                </li>
                <li>
                    <hr/>
                    <PanelHeader text="Estimated Net Cost" value={finalNetCost} leader={true}
                                 helpText={RESULT_NET_COST_HELP_TEXT}/>
                </li>
            </ul>
        </div>
    );
}

/**
 * Modifies the given Student Aid Index (SAI) based on certain conditions.
 *
 * @param {number} oldSAI - The original Student Aid Index (SAI) value.
 * @returns {number} - The Poverty Threshold modified Student Aid Index (SAI) value.
 */
const ModifySAIPovertyThreshold = (oldSAI) => {

    const context = useContext(ReactReduxContext);
    const data = () => context.store.getState().app.stepData;
    const val = data();

    let dep = (val.step1.status !== STATUS_INDEPENDENT)
        ? STATUS_DEPENDENT
        : STATUS_INDEPENDENT;

    let maritalStatus = (val.step3.studentMaritalStatus !== STUDENT_MARITAL_STATUS_MARRIED)
        ? STUDENT_MARITAL_STATUS_SINGLE
        : STUDENT_MARITAL_STATUS_MARRIED;

    let familySize = parseInt(val.step3.familySize);

    let hasDependents = (val.step3.studentHasDependents !== HAS_DEPENDENTS_YES)
        ? HAS_DEPENDENTS_NO
        : HAS_DEPENDENTS_YES;

    let agi = (val.step1.status === STATUS_DEPENDENT)
        ? parseInt(val.step3.parentAdjustedGrossIncome)
        : parseInt(val.step3.studentAdjustedGrossIncome);

    let saic = oldSAI;

    let basePovertyMultiplier = 4720;
    let zeroFamilySizeBase = 8870;

    let basePovertyRate = zeroFamilySizeBase + basePovertyMultiplier * familySize;
    let new175 = Math.round(basePovertyRate * 1.75);
    let new225 = Math.round(basePovertyRate * 2.25);

    let parentMaritalStatus = parseInt(val.step3.parentMaritalStatus) === PARENT_MARITAL_STATUS_MARRIED
        ? PARENT_MARITAL_STATUS_MARRIED
        : parseInt(val.step3.parentMaritalStatus) === PARENT_MARITAL_STATUS_SINGLE
            ? PARENT_MARITAL_STATUS_SINGLE
            : 0;

    if (oldSAI > 0 && dep === STATUS_DEPENDENT && parentMaritalStatus === PARENT_MARITAL_STATUS_SINGLE && agi <= new225) {
        saic = 0;
    }
    if (oldSAI > 0 && dep === STATUS_DEPENDENT && parentMaritalStatus === PARENT_MARITAL_STATUS_MARRIED && agi <= new175) {
        saic = 0;
    }
    if (oldSAI > 0 && dep === STATUS_INDEPENDENT && maritalStatus === STUDENT_MARITAL_STATUS_SINGLE &&
        hasDependents === HAS_DEPENDENTS_YES && agi <= new225) {
        saic = 0;
    }
    if (oldSAI > 0 && dep === STATUS_INDEPENDENT && maritalStatus === STUDENT_MARITAL_STATUS_MARRIED &&
        hasDependents === HAS_DEPENDENTS_YES && agi <= new175) {
        saic = 0;
    }
    if (oldSAI > 0 && dep === STATUS_INDEPENDENT && hasDependents === HAS_DEPENDENTS_NO && agi <= new175) {
        saic = 0;
    }

    if (DEBUG_MODE === true) {
        let debugMaritalStatus =
            (dep === STATUS_DEPENDENT)
                ? (parentMaritalStatus === PARENT_MARITAL_STATUS_SINGLE)
                    ? "Dependent - Single parent"
                    : (parentMaritalStatus === PARENT_MARITAL_STATUS_MARRIED)
                        ? "Dependent - Married parents"
                        : `Unknown marital value: Dependent - ${parentMaritalStatus}`
                : (dep === STATUS_INDEPENDENT)
                    ? (maritalStatus === STUDENT_MARITAL_STATUS_SINGLE)
                        ? "Independent - Single"
                        : (maritalStatus === STUDENT_MARITAL_STATUS_MARRIED)
                            ? "Independent - Married"
                            : `Unknown marital value: Independent - ${maritalStatus}`
                    : {
                        "Unknown": {
                            "Dependency value": dep,
                            "Parent Marital Status Value": parentMaritalStatus,
                            "Student Marital Status Value": maritalStatus
                        }
                    };

        let debugIndependence =
            (dep === STATUS_DEPENDENT)
                ? `Dependent: ${STATUS_DEPENDENT}`
                : (dep === STATUS_INDEPENDENT)
                    ? `Independent: ${STATUS_INDEPENDENT}`
                    : `Unknown dependency value: ${dep}`

        let debugHasDependents =
            (hasDependents === HAS_DEPENDENTS_YES)
                ? "Dependents - Yes"
                : (hasDependents === HAS_DEPENDENTS_NO)
                    ? "Dependents - No"
                    : `Unknown dependents value - ${hasDependents}`;

        let debugParentAGI = (val.step1.status === STATUS_DEPENDENT)
            ? parseInt(val.step3.parentAdjustedGrossIncome)
            : 0;

        let debugStudentAGI = (val.step1.status !== STATUS_DEPENDENT)
            ? parseInt(val.step3.studentAdjustedGrossIncome)
            : 0;

        let debugBlock = new faeDebugger('SAI Modifications');
        let blockCalculations = new faeDebugger('Calculations');

        debugBlock.addLine('Dependency Status', debugIndependence);
        debugBlock.addLine('Marital Status', debugMaritalStatus);
        debugBlock.addLine('Family Size', familySize);
        debugBlock.addLine('Has Dependents', debugHasDependents);
        debugBlock.addLine('Parent AGI', debugParentAGI);
        debugBlock.addLine('Student AGI', debugStudentAGI);
        debugBlock.addLine('SAIC', saic);
        debugBlock.addLine('SAI', oldSAI);
        debugBlock.addLine('Base Poverty Rate', basePovertyRate);
        debugBlock.addLine('175 percent', new175);
        debugBlock.addLine('225 percent', new225);

        debugBlock.addBlock('00', blockCalculations);
        blockCalculations.addLine(
            'SAI > 0, Dependent, Parent Single, ParentAGI < 225 percent',
            (dep === STATUS_DEPENDENT && parentMaritalStatus === PARENT_MARITAL_STATUS_SINGLE && agi <= new225));
        blockCalculations.addLine(
            'SAI > 0, Dependent, Parents Married, ParentAGI < 175 percent',
            (dep === STATUS_DEPENDENT && parentMaritalStatus === PARENT_MARITAL_STATUS_SINGLE && agi <= new175));
        blockCalculations.addLine(
            'SAI > 0, Independent, Single, Has dependents, StudentAGI < 225 percent',
            (dep === STATUS_INDEPENDENT && maritalStatus === STUDENT_MARITAL_STATUS_SINGLE &&
            hasDependents === HAS_DEPENDENTS_YES && agi <= new225));
        blockCalculations.addLine(
            'SAI > 0, Independent, Married, Has dependents, StudentAGI < 175 percent',
            (dep === STATUS_INDEPENDENT && maritalStatus === STUDENT_MARITAL_STATUS_MARRIED &&
            hasDependents === HAS_DEPENDENTS_YES && agi <= new175));
        blockCalculations.addLine(
            'SAI > 0, Independent, No dependents, StudentAGI < 175 percent',
            (dep === STATUS_INDEPENDENT && hasDependents === HAS_DEPENDENTS_NO && agi <= new175));

        debugBlock.toGroup();
    }

    return saic;
}
/**
 * Retrieves the "Self Help" modifier ini array form.  This is determined
 * by whether a student is a California resident, and the Student Aid Index (SAI).
 *
 * @param data {Object} - state data for lookup of step2 values.
 * @param povertyModifiedSAI {number}
 * @returns {number[]} - An array containing the range of Estimated Gift Eligibility modifiers.
 */
const step4CalculateEge = (data, povertyModifiedSAI) => {
    //  Returned as an array so all processing of returned value is on an array
    if (povertyModifiedSAI === -1500) {
        return [
            parseInt(ATTENDANCE_COSTS[data.residency][data.campus][SELF_HELP][SELF_HELP_DEBT_FREE])
        ];
    }

    return [
        parseInt(ATTENDANCE_COSTS[data.residency][data.campus][SELF_HELP][SELF_HELP_RANGE_LOW]),
        parseInt(ATTENDANCE_COSTS[data.residency][data.campus][SELF_HELP][SELF_HELP_RANGE_HIGH])
    ];
}